import React, { useEffect, useRef, useState } from 'react';
import { Check, Copy, Search } from 'lucide-react';
import { useAppendix } from '../context/AppendixContext';
import { isSpanishMobilePhone, normalizePhone } from '../utils/phone';

const CopyablePhone = ({
    phone,
    children,
    className = '',
    textClassName = '',
    buttonClassName = '',
    searchButtonClassName = '',
    showText = true,
    showSearch = true
}) => {
    const { searchLeadByPhone } = useAppendix();
    const [copied, setCopied] = useState(false);
    const copiedTimerRef = useRef(null);
    const normalizedPhone = normalizePhone(phone);
    const canSearchLeads = isSpanishMobilePhone(phone);
    const displayValue = children ?? normalizedPhone;
    const displayText = typeof displayValue === 'object'
        ? displayValue?.name || displayValue?.display_phone_number || displayValue?.phone_number || ''
        : displayValue;

    useEffect(() => () => clearTimeout(copiedTimerRef.current), []);

    if (!phone) return children || null;

    const stopEvent = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const handleCopy = async (event) => {
        stopEvent(event);
        if (!normalizedPhone || !navigator.clipboard?.writeText) return;

        try {
            await navigator.clipboard.writeText(normalizedPhone);
            setCopied(true);
            clearTimeout(copiedTimerRef.current);
            copiedTimerRef.current = setTimeout(() => setCopied(false), 1500);
        } catch (error) {
            console.error('No se pudo copiar el teléfono:', error);
        }
    };

    const handleLeadSearch = (event) => {
        stopEvent(event);
        if (canSearchLeads) searchLeadByPhone(normalizedPhone);
    };

    return (
        <span className={`inline-flex min-w-0 items-center gap-1 ${className}`}>
            {showText && (
                <span className={`min-w-0 truncate ${textClassName}`} title={String(normalizedPhone)}>
                    {displayText}
                </span>
            )}
            <button
                type="button"
                onClick={handleCopy}
                className={`shrink-0 rounded p-0.5 text-slate-500 transition-colors hover:bg-slate-700 hover:text-slate-100 focus:outline-none focus:ring-1 focus:ring-primary ${copied ? 'text-emerald-400' : ''} ${buttonClassName}`}
                title={copied ? 'Teléfono copiado' : 'Copiar teléfono'}
                aria-label={copied ? 'Teléfono copiado' : 'Copiar teléfono'}
            >
                {copied ? <Check size={12} /> : <Copy size={12} />}
            </button>
            {showSearch && (
                <button
                    type="button"
                    onClick={handleLeadSearch}
                    disabled={!canSearchLeads}
                    className={`shrink-0 rounded p-0.5 text-slate-500 transition-colors hover:bg-slate-700 hover:text-sky-300 focus:outline-none focus:ring-1 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-40 ${searchButtonClassName}`}
                    title={canSearchLeads ? 'Buscar lead por teléfono' : 'El teléfono no tiene un formato español válido'}
                    aria-label="Buscar lead por teléfono"
                >
                    <Search size={12} />
                </button>
            )}
        </span>
    );
};

export default CopyablePhone;